import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, MousePointer2, Target, Eye, Copy, Check, Crosshair, Scan } from 'lucide-react';

const PageInspector = ({ isActive, onSelect, onClose }) => {
  const [hoveredElement, setHoveredElement] = useState(null);
  const [hoveredRect, setHoveredRect] = useState(null);
  const [selectedElements, setSelectedElements] = useState([]);
  const [drilledContainer, setDrilledContainer] = useState(null); // Container we've "entered"
  const [drillPath, setDrillPath] = useState([]); // Breadcrumb of drilled containers
  const [zIndexDepth, setZIndexDepth] = useState(0); // Current depth in element stack (Shift+Click to go deeper)
  const [elementsAtPoint, setElementsAtPoint] = useState([]); // All elements at current mouse position
  const overlayRef = useRef(null);

  // Check if element should be ignored (overlays, layout wrappers, etc.)
  const shouldIgnoreElement = (element) => {
    if (!element) return true;

    const tagName = element.tagName?.toLowerCase();

    // Always ignore html and body for selection
    if (tagName === 'body' || tagName === 'html') return true;

    // Use classList for reliable class checking
    const classList = element.classList;
    const classString = getClassNameString(element).toLowerCase();

    if (classList) {
      // IGNORE: Any element with "fixed" and "inset-0" classes (full screen overlays)
      if (classList.contains('fixed') && classList.contains('inset-0')) {
        return true;
      }

      // IGNORE: Any element with "absolute" and "inset-0"
      if (classList.contains('absolute') && classList.contains('inset-0')) {
        return true;
      }

      // IGNORE: Layout wrapper classes
      if (classList.contains('App') ||
          classList.contains('min-h-screen') ||
          classList.contains('flex-1')) {
        // Check if it has meaningful content - if just a wrapper, ignore it
        const directTextContent = Array.from(element.childNodes)
          .filter(n => n.nodeType === Node.TEXT_NODE)
          .map(n => n.textContent.trim())
          .join('');
        if (!directTextContent && element.children.length > 0) {
          return true; // Pure wrapper element
        }
      }
    }

    // Check for inset-0 in class string (handles variations)
    if (classString.includes('inset-0') &&
        (classString.includes('fixed') || classString.includes('absolute'))) {
      return true;
    }

    // Check computed styles as backup
    const style = window.getComputedStyle(element);
    const isFixed = style.position === 'fixed';
    const isAbsolute = style.position === 'absolute';

    // Check if element covers full viewport via computed styles
    if ((isFixed || isAbsolute) && style.inset === '0px') {
      return true;
    }

    // Check bounding rect for large layout elements
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const coversFullViewport =
      rect.width >= viewportWidth * 0.95 &&
      rect.height >= viewportHeight * 0.95;

    // Large fixed/absolute elements covering viewport are overlays
    if (coversFullViewport && (isFixed || isAbsolute)) {
      return true;
    }

    // Large elements with no specific semantic purpose (just layout wrappers)
    const coversLargeArea = rect.width >= viewportWidth * 0.9 && rect.height >= viewportHeight * 0.9;
    if (coversLargeArea && tagName === 'div') {
      // Check if it's just a layout div (no id, no semantic classes)
      const hasSemanticPurpose = element.id ||
        classString.includes('card') ||
        classString.includes('panel') ||
        classString.includes('modal') ||
        classString.includes('dialog') ||
        classString.includes('content') ||
        classString.includes('container') ||
        classString.includes('section');

      if (!hasSemanticPurpose) {
        return true;
      }
    }

    return false;
  };

  // Get the actual element under cursor, skipping overlays
  const getElementAtPoint = (x, y) => {
    // STEP 1: Temporarily hide all fixed inset-0 overlays
    const overlaysToHide = document.querySelectorAll('.fixed.inset-0, [class*="fixed"][class*="inset-0"]');
    const hiddenOverlays = [];

    overlaysToHide.forEach(overlay => {
      // Don't hide our own inspector overlay
      if (overlay.closest('.page-inspector-overlay')) return;

      // Store original style and hide
      hiddenOverlays.push({
        el: overlay,
        pointerEvents: overlay.style.pointerEvents,
        visibility: overlay.style.visibility
      });
      overlay.style.pointerEvents = 'none';
      overlay.style.visibility = 'hidden';
    });

    // STEP 2: Now get elements at point (overlays are hidden)
    const elements = document.elementsFromPoint(x, y);

    // STEP 3: Restore overlays immediately
    hiddenOverlays.forEach(({ el, pointerEvents, visibility }) => {
      el.style.pointerEvents = pointerEvents;
      el.style.visibility = visibility;
    });

    let bestElement = null;
    let smallestArea = Infinity;

    for (const el of elements) {
      // Skip inspector overlay
      if (el.closest('.page-inspector-overlay')) continue;

      // If we're drilled into a container, only consider its descendants
      if (drilledContainer && !drilledContainer.contains(el)) {
        continue;
      }

      // Skip the drilled container itself - we want its children
      if (drilledContainer && el === drilledContainer) {
        continue;
      }

      // Skip full-screen overlays (unless we're drilled into them)
      if (!drilledContainer && shouldIgnoreElement(el)) continue;

      // Skip elements with pointer-events: none (check computed, not inline)
      const style = window.getComputedStyle(el);
      if (style.pointerEvents === 'none') continue;

      // Skip invisible elements
      if (style.visibility === 'hidden' || style.display === 'none') continue;

      // Calculate element area - prefer smaller, more specific elements
      const rect = el.getBoundingClientRect();
      const area = rect.width * rect.height;

      // Skip elements with zero area
      if (area === 0) continue;

      // Prefer the smallest visible element that contains the point
      if (area < smallestArea) {
        smallestArea = area;
        bestElement = el;
      }
    }

    // If we found a good element, return it
    if (bestElement) return bestElement;

    // Fallback: return first non-overlay element
    for (const el of elements) {
      if (!el.closest('.page-inspector-overlay') && !shouldIgnoreElement(el)) {
        // Respect drill mode in fallback too
        if (drilledContainer && !drilledContainer.contains(el)) continue;
        if (drilledContainer && el === drilledContainer) continue;
        return el;
      }
    }

    return null;
  };

  // Drill into a container element (double-click)
  const drillIntoElement = (element) => {
    if (!element) return;

    // Add current container to breadcrumb path
    if (drilledContainer) {
      setDrillPath(prev => [...prev, drilledContainer]);
    }

    // Set new drilled container
    setDrilledContainer(element);
    setHoveredElement(null);
    setHoveredRect(null);
  };

  // Exit drill mode (go back one level)
  const exitDrillMode = () => {
    if (drillPath.length > 0) {
      // Go back to previous container
      const newPath = [...drillPath];
      const previousContainer = newPath.pop();
      setDrillPath(newPath);
      setDrilledContainer(previousContainer);
    } else {
      // Exit drill mode completely
      setDrilledContainer(null);
    }
    setHoveredElement(null);
    setHoveredRect(null);
  };

  // Reset drill mode completely
  const resetDrillMode = () => {
    setDrilledContainer(null);
    setDrillPath([]);
    setHoveredElement(null);
    setHoveredRect(null);
  };

  // Safely get className as string (handles SVG elements)
  const getClassNameString = (el) => {
    if (!el) return '';
    const cn = el.className;
    if (typeof cn === 'string') return cn;
    if (cn && typeof cn.baseVal === 'string') return cn.baseVal;
    return '';
  };

  // Get ALL elements at a point (including overlays) - for z-index navigation
  const getAllElementsAtPoint = useCallback((x, y) => {
    const elements = document.elementsFromPoint(x, y);
    return elements.filter(el => {
      if (el.closest('.page-inspector-overlay')) return false;
      const tag = el.tagName?.toLowerCase();
      if (tag === 'html' || tag === 'body') return false;
      return true;
    });
  }, []);

  // Extract meaningful data from an element
  const extractElementData = useCallback((element) => {
    if (!element) return null;

    const classNameStr = getClassNameString(element);
    const data = {
      tagName: element.tagName?.toLowerCase(),
      id: element.id || null,
      className: classNameStr || null,
      text: element.innerText?.substring(0, 500) || null,
      value: element.value || null,
      href: element.href || null,
      src: element.src || null,
      alt: element.alt || null,
      title: element.title || null,
      placeholder: element.placeholder || null,
      type: element.type || null,
      name: element.name || null,
      role: element.getAttribute?.('role') || null,
      ariaLabel: element.getAttribute?.('aria-label') || null,
      dataAttributes: {},
      computedStyles: {},
      rect: null,
      path: getElementPath(element),
    };

    // Extract data attributes
    if (element.dataset) {
      Object.keys(element.dataset).forEach(key => {
        data.dataAttributes[key] = element.dataset[key];
      });
    }

    // Get bounding rect
    const rect = element.getBoundingClientRect();
    data.rect = {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    };

    // Extract table data if it's a table
    if (element.tagName === 'TABLE' || element.closest('table')) {
      const table = element.tagName === 'TABLE' ? element : element.closest('table');
      data.tableData = extractTableData(table);
    }

    // Extract form data if it's a form or inside a form
    if (element.tagName === 'FORM' || element.closest('form')) {
      const form = element.tagName === 'FORM' ? element : element.closest('form');
      data.formData = extractFormData(form);
    }

    // Extract card/panel data
    if (element.classList?.contains('card') || element.closest('[class*="card"]')) {
      const card = element.classList?.contains('card') ? element : element.closest('[class*="card"]');
      data.cardData = extractCardData(card);
    }

    // Extract chart/graph data if present
    const chartContainer = element.closest('[class*="chart"]') || element.closest('[class*="graph"]');
    if (chartContainer) {
      data.chartContext = extractChartContext(chartContainer);
    }

    // Extract vulnerability data if present
    if (element.closest('[class*="vulnerability"]') || element.closest('[class*="vuln"]')) {
      data.vulnerabilityContext = extractVulnerabilityContext(element);
    }

    // Extract scan data if present
    if (element.closest('[class*="scan"]')) {
      data.scanContext = extractScanContext(element);
    }

    return data;
  }, []);

  // Get element path (breadcrumb)
  const getElementPath = (element) => {
    const path = [];
    let current = element;
    while (current && current !== document.body) {
      let selector = current.tagName?.toLowerCase();
      if (current.id) {
        selector += `#${current.id}`;
      } else if (getClassNameString(current)) {
        const classes = getClassNameString(current).split(' ').filter(c => c && !c.startsWith('hover')).slice(0, 2);
        if (classes.length) {
          selector += `.${classes.join('.')}`;
        }
      }
      path.unshift(selector);
      current = current.parentElement;
    }
    return path.slice(-4).join(' > ');
  };

  // Extract table data
  const extractTableData = (table) => {
    if (!table) return null;
    const headers = [];
    const rows = [];

    // Get headers
    const headerCells = table.querySelectorAll('th');
    headerCells.forEach(th => {
      headers.push(th.innerText?.trim());
    });

    // Get first 5 rows of data
    const bodyRows = table.querySelectorAll('tbody tr');
    bodyRows.forEach((row, idx) => {
      if (idx < 5) {
        const cells = [];
        row.querySelectorAll('td').forEach(td => {
          cells.push(td.innerText?.trim()?.substring(0, 100));
        });
        rows.push(cells);
      }
    });

    return { headers, rows, totalRows: bodyRows.length };
  };

  // Extract form data
  const extractFormData = (form) => {
    if (!form) return null;
    const fields = [];
    form.querySelectorAll('input, select, textarea').forEach(input => {
      fields.push({
        name: input.name || input.id,
        type: input.type,
        value: input.type === 'password' ? '***' : input.value?.substring(0, 100),
        placeholder: input.placeholder,
        required: input.required,
      });
    });
    return { fields };
  };

  // Extract card data
  const extractCardData = (card) => {
    if (!card) return null;
    return {
      title: card.querySelector('h1, h2, h3, h4, h5, h6, [class*="title"]')?.innerText?.substring(0, 100),
      content: card.innerText?.substring(0, 500),
    };
  };

  // Extract chart context
  const extractChartContext = (container) => {
    if (!container) return null;
    return {
      type: 'chart',
      title: container.querySelector('[class*="title"]')?.innerText,
      labels: container.getAttribute('data-labels'),
    };
  };

  // Extract vulnerability context
  const extractVulnerabilityContext = (element) => {
    const container = element.closest('[class*="vulnerability"]') || element.closest('[class*="vuln"]') || element;
    return {
      severity: container.querySelector('[class*="severity"]')?.innerText ||
                container.querySelector('[class*="critical"], [class*="high"], [class*="medium"], [class*="low"]')?.innerText,
      cve: container.innerText?.match(/CVE-\d{4}-\d+/)?.[0],
      title: container.querySelector('[class*="title"], h3, h4')?.innerText?.substring(0, 200),
      description: container.querySelector('[class*="description"], p')?.innerText?.substring(0, 500),
    };
  };

  // Extract scan context
  const extractScanContext = (element) => {
    const container = element.closest('[class*="scan"]') || element;
    return {
      status: container.querySelector('[class*="status"]')?.innerText,
      target: container.querySelector('[class*="target"]')?.innerText,
      type: container.querySelector('[class*="type"]')?.innerText,
      progress: container.querySelector('[class*="progress"]')?.innerText ||
                container.querySelector('progress')?.value,
    };
  };

  // Handle mouse move
  const handleMouseMove = useCallback((e) => {
    if (!isActive) return;

    // Ignore elements in the inspector overlay itself
    if (e.target.closest('.page-inspector-overlay')) return;

    const element = getElementAtPoint(e.clientX, e.clientY);
    if (element && element !== hoveredElement) {
      setHoveredElement(element);
      const rect = element.getBoundingClientRect();
      setHoveredRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  }, [isActive, hoveredElement]);

  // Get meaningful children of an element (skip text nodes, scripts, styles)
  const getMeaningfulChildren = useCallback((element) => {
    if (!element) return [];
    const children = Array.from(element.children).filter(child => {
      const tag = child.tagName.toLowerCase();
      if (['script', 'style', 'noscript', 'template'].includes(tag)) return false;
      if (child.closest('.page-inspector-overlay')) return false;
      // Skip hidden elements
      const style = window.getComputedStyle(child);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      return true;
    });
    return children;
  }, []);

  // Handle single click to select element
  // Shift+Click = select child element at this point (go deeper into DOM / z-index)
  // Ctrl+Click = select parent (go up in DOM)
  const handleClick = useCallback((e) => {
    if (!isActive) return;

    // Ignore clicks on the overlay controls
    if (e.target.closest('.page-inspector-overlay')) return;

    e.preventDefault();
    e.stopPropagation();

    // Get ALL elements at point (including overlays) - ordered from top to bottom
    const allElements = getAllElementsAtPoint(e.clientX, e.clientY);
    setElementsAtPoint(allElements);

    // Current depth in z-index stack
    let currentDepth = zIndexDepth;

    // Shift+Click: go DEEPER in the z-index stack (next element behind)
    if (e.shiftKey) {
      currentDepth = Math.min(currentDepth + 1, allElements.length - 1);
      setZIndexDepth(currentDepth);

      // Get element at new depth
      const element = allElements[currentDepth];
      if (element) {
        setHoveredElement(element);
        const rect = element.getBoundingClientRect();
        setHoveredRect(rect);
      }
      return; // Don't select on navigation
    }

    // Ctrl+Click: go UP in the z-index stack (element in front)
    if (e.ctrlKey || e.metaKey) {
      currentDepth = Math.max(currentDepth - 1, 0);
      setZIndexDepth(currentDepth);

      // Get element at new depth
      const element = allElements[currentDepth];
      if (element) {
        setHoveredElement(element);
        const rect = element.getBoundingClientRect();
        setHoveredRect(rect);
      }
      return; // Don't select on navigation
    }

    // Regular click: select element at current depth
    const element = allElements[currentDepth];
    if (!element) return;

    // Update hover to show the selected element
    setHoveredElement(element);
    const rect = element.getBoundingClientRect();
    setHoveredRect(rect);

    // Add to selection
    const elementData = extractElementData(element);
    if (elementData) {
      setSelectedElements(prev => [...prev, elementData]);
    }

    // Reset depth after selection
    setZIndexDepth(0);
  }, [isActive, extractElementData, getAllElementsAtPoint, zIndexDepth]);

  // Handle double-click to drill into element (set as container)
  const handleDoubleClick = useCallback((e) => {
    if (!isActive) return;

    // Ignore clicks on the overlay controls
    if (e.target.closest('.page-inspector-overlay')) return;

    e.preventDefault();
    e.stopPropagation();

    // Get all elements at point
    const elements = getAllElementsAtPoint(e.clientX, e.clientY);
    if (elements.length === 0) return;

    // Use current z-index depth to determine which element to drill into
    const targetElement = elements[zIndexDepth] || elements[0];

    if (targetElement) {
      // Remove this element from selection if it was selected
      setSelectedElements(prev => prev.filter(sel => {
        const path = getElementPath(targetElement);
        return sel.path !== path;
      }));

      // Drill into the element
      drillIntoElement(targetElement);
      // Reset depth when drilling
      setZIndexDepth(0);
    }
  }, [isActive, zIndexDepth, getAllElementsAtPoint]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if (!isActive) return;

    if (e.key === 'Escape') {
      // If in drill mode, exit drill mode first
      if (drilledContainer) {
        exitDrillMode();
      } else {
        onClose();
      }
    }

    // Backspace to go back in drill mode
    if (e.key === 'Backspace' && drilledContainer) {
      e.preventDefault();
      exitDrillMode();
    }

    // Enter to select currently hovered element
    if (e.key === 'Enter' && hoveredElement) {
      e.preventDefault();
      const elementData = extractElementData(hoveredElement);
      if (elementData) {
        setSelectedElements(prev => [...prev, elementData]);
      }
      // Reset depth after selection
      setZIndexDepth(0);
    }
  }, [isActive, onClose, drilledContainer, hoveredElement, extractElementData]);

  // Set up event listeners
  useEffect(() => {
    if (isActive) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('click', handleClick, true);
      document.addEventListener('dblclick', handleDoubleClick, true);
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.cursor = 'crosshair';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('click', handleClick, true);
        document.removeEventListener('dblclick', handleDoubleClick, true);
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.cursor = '';
      };
    }
  }, [isActive, handleMouseMove, handleClick, handleDoubleClick, handleKeyDown]);

  // Confirm selection and close
  const confirmSelection = () => {
    if (selectedElements.length > 0) {
      onSelect(selectedElements);
    }
    onClose();
  };

  // Remove a selected element
  const removeSelection = (index) => {
    setSelectedElements(prev => prev.filter((_, i) => i !== index));
  };

  if (!isActive) return null;

  return (
    <>
      {/* Highlight overlay for hovered element */}
      {hoveredRect && (
        <div
          className="fixed pointer-events-none z-[9998] border-2 border-purple-500 bg-purple-500/20 transition-all duration-75"
          style={{
            top: hoveredRect.top,
            left: hoveredRect.left,
            width: hoveredRect.width,
            height: hoveredRect.height,
          }}
        >
          <div className="absolute -top-6 left-0 bg-purple-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
            {hoveredElement?.tagName?.toLowerCase()}
            {hoveredElement?.id && `#${hoveredElement.id}`}
            {getClassNameString(hoveredElement) &&
              `.${getClassNameString(hoveredElement).split(' ')[0]}`}
          </div>
        </div>
      )}

      {/* Selected elements highlights */}
      {selectedElements.map((el, idx) => el.rect && (
        <div
          key={idx}
          className="fixed pointer-events-none z-[9997] border-2 border-green-500 bg-green-500/20"
          style={{
            top: el.rect.top,
            left: el.rect.left,
            width: el.rect.width,
            height: el.rect.height,
          }}
        >
          <div className="absolute -top-6 right-0 bg-green-600 text-white text-xs px-2 py-1 rounded">
            #{idx + 1}
          </div>
        </div>
      ))}

      {/* Control panel */}
      <div
        ref={overlayRef}
        className="page-inspector-overlay fixed top-4 left-1/2 -translate-x-1/2 z-[9999] bg-purple-900/95 backdrop-blur-sm border border-purple-500/50 rounded-xl shadow-2xl p-4 min-w-[400px] max-w-[600px]"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-600 rounded-lg">
              <Crosshair className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold">Mode Inspecteur</h3>
              <p className="text-purple-300 text-xs">Click on elements to select them</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-purple-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Drill Mode Indicator */}
        {drilledContainer && (
          <div className="bg-yellow-900/50 border border-yellow-500/50 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-yellow-300 text-sm">
                <Target className="w-4 h-4" />
                <span>Focus mode enabled</span>
              </div>
              <button
                onClick={resetDrillMode}
                className="px-2 py-1 bg-yellow-600/30 hover:bg-yellow-600/50 text-yellow-200 rounded text-xs transition-colors"
              >
                Quitter le focus
              </button>
            </div>
            <p className="text-yellow-200/70 text-xs mt-1 font-mono truncate">
              {getClassNameString(drilledContainer).split(' ').slice(0, 3).join('.')}
            </p>
          </div>
        )}

        {/* Current Element Info with Z-Index Depth */}
        {hoveredElement && (
          <div className="bg-blue-900/50 border border-blue-500/50 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-blue-300 text-sm">
                <Scan className="w-4 h-4" />
                <span>Current element</span>
              </div>
              <span className="bg-blue-600/50 text-blue-200 text-xs px-2 py-0.5 rounded-full">
                Profondeur: {zIndexDepth + 1} / {elementsAtPoint.length || 1}
              </span>
            </div>
            <p className="text-blue-200/70 text-xs mt-1 font-mono truncate">
              {hoveredElement.tagName.toLowerCase()}
              {hoveredElement.id && `#${hoveredElement.id}`}
              .{getClassNameString(hoveredElement).split(' ').slice(0, 2).join('.')}
            </p>
            {elementsAtPoint.length > 1 && (
              <p className="text-green-400/70 text-xs mt-1">
                → Shift+Clic pour descendre · Ctrl+Clic pour remonter
              </p>
            )}
          </div>
        )}

        {/* Instructions */}
        <div className="bg-purple-800/50 rounded-lg p-3 mb-4 text-sm text-purple-200">
          <div className="flex items-center gap-2 mb-1">
            <MousePointer2 className="w-4 h-4" />
            <span><strong>Click</strong> = select the visible element</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <Scan className="w-4 h-4 text-green-400" />
            <span><strong>Shift+Click</strong> = element below (z-index) ↓</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-yellow-400" />
            <span><strong>Ctrl+Click</strong> = element above (z-index) ↑</span>
          </div>
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-blue-400" />
            <span><strong>Double-clic</strong> = focus dans conteneur</span>
          </div>
        </div>

        {/* Selected elements list */}
        {selectedElements.length > 0 && (
          <div className="mb-4">
            <h4 className="text-white text-sm font-medium mb-2 flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              Selected elements ({selectedElements.length})
            </h4>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {selectedElements.map((el, idx) => (
                <div
                  key={idx}
                  className="bg-purple-800/50 rounded-lg p-2 flex items-start justify-between group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-green-400 font-mono text-xs">#{idx + 1}</span>
                      <span className="text-purple-300 font-mono text-xs">{el.path}</span>
                    </div>
                    {el.text && (
                      <p className="text-white text-xs mt-1 truncate">
                        {el.text.substring(0, 100)}...
                      </p>
                    )}
                    {el.tableData && (
                      <p className="text-purple-300 text-xs mt-1">
                        Table: {el.tableData.totalRows} lignes, {el.tableData.headers?.length} colonnes
                      </p>
                    )}
                    {el.vulnerabilityContext?.cve && (
                      <p className="text-orange-400 text-xs mt-1">
                        {el.vulnerabilityContext.cve} - {el.vulnerabilityContext.severity}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => removeSelection(idx)}
                    className="p-1 hover:bg-red-500/30 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-purple-800 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
          >
            Annuler
          </button>
          <button
            onClick={confirmSelection}
            disabled={selectedElements.length === 0}
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-purple-800 disabled:opacity-50 text-white rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
          >
            <Check className="w-4 h-4" />
            Confirmer ({selectedElements.length})
          </button>
        </div>

        {/* Keyboard shortcut hint */}
        <p className="text-center text-purple-400 text-xs mt-3">
          <kbd className="bg-purple-800 px-1.5 py-0.5 rounded">Enter</kbd> select
          {' · '}
          <kbd className="bg-purple-800 px-1.5 py-0.5 rounded">Esc</kbd> {drilledContainer ? 'exit focus' : 'cancel'}
          {drilledContainer && (
            <> · <kbd className="bg-purple-800 px-1.5 py-0.5 rounded">⌫</kbd> retour</>
          )}
        </p>
      </div>
    </>
  );
};

export default PageInspector;
